/**
 * ExecutionSignalClusters
 * ────────────────────────
 * Compact read-only summary of clustered execution patterns. Renders
 * above the Live Stream rail. No actions, no scoring, no AI — pure
 * observational chips grouped by installer / job / scheduling.
 */
import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { useAppData } from "@/data/AppDataContext";
import { useVendorPO } from "@/data/VendorPOContext";
import {
  buildExecutionSignalClusters,
  SIGNAL_CLUSTER_GROUP_LABEL,
  type SignalCluster,
  type SignalClusterGroup,
} from "@/data/execution/signalClusters";
import { openResolutionDrawer } from "@/data/execution/resolutionDrawerBus";
import { Layers, User, Briefcase, CalendarClock } from "lucide-react";

const GROUP_ICON: Record<SignalClusterGroup, React.ComponentType<{ className?: string }>> = {
  installer: User,
  job: Briefcase,
  scheduling: CalendarClock,
};

const MAX_PER_GROUP = 4;

export function ExecutionSignalClusters() {
  const { specExecutions, contractors, jobs, callbacks, holdbackLedger } = useAppData();
  const { fieldMaterialFeedback } = useVendorPO();

  const jobInstallerMap = useMemo(() => {
    const out: Record<string, string | null | undefined> = {};
    for (const j of jobs) {
      out[j.id] = j.ownerContractorId ?? null;
    }
    return out;
  }, [jobs]);

  const clusters = useMemo(
    () =>
      buildExecutionSignalClusters({
        callbacks,
        holdbackLedger,
        fieldMaterialFeedback: fieldMaterialFeedback ?? [],
        specExecutions,
        contractors,
        jobInstallerMap,
      }),
    [callbacks, holdbackLedger, fieldMaterialFeedback, specExecutions, contractors, jobInstallerMap],
  );

  if (clusters.total === 0) return null;

  const groups: { key: SignalClusterGroup; items: SignalCluster[] }[] = (
    [
      { key: "installer" as const, items: clusters.installer },
      { key: "job" as const, items: clusters.job },
      { key: "scheduling" as const, items: clusters.scheduling },
    ]
  ).filter((g) => g.items.length > 0);

  return (
    <Card
      data-testid="execution-signal-clusters"
      className="border-border/60 shadow-sm"
    >
      <div className="px-3 py-2 border-b border-border/60 flex items-center gap-1.5">
        <Layers className="h-3 w-3 text-muted-foreground" />
        <h3 className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          Patterns
        </h3>
        <span className="ml-auto text-[10px] text-muted-foreground" data-testid="cluster-total">
          {clusters.total}
        </span>
      </div>
      <div className="px-3 py-2 space-y-2.5">
        {groups.map((g) => {
          const Icon = GROUP_ICON[g.key];
          const items = g.items.slice(0, MAX_PER_GROUP);
          return (
            <div key={g.key} data-testid={`cluster-group-${g.key}`}>
              <div className="flex items-center gap-1 mb-1 text-[10px] font-semibold text-muted-foreground">
                <Icon className="h-3 w-3" />
                <span>{SIGNAL_CLUSTER_GROUP_LABEL[g.key]}</span>
              </div>
              <ul className="space-y-0.5">
                {items.map((c) => {
                  const jobId = c.kind === "job_hotspot" ? c.id.replace(/^job-hot:/, "") : null;
                  const clickable = jobId !== null;
                  return (
                    <li
                      key={c.id}
                      data-testid="cluster-item"
                      data-kind={c.kind}
                      className={`text-[11px] leading-tight truncate ${clickable ? "cursor-pointer hover:text-primary" : ""}`}
                      title={clickable ? `${c.label} — open resolution drawer` : c.label}
                      onClick={
                        clickable
                          ? () =>
                              openResolutionDrawer({
                                jobId: jobId!,
                                source: "signal_cluster",
                                eventContext: {
                                  kind: c.kind,
                                  headline: c.label,
                                  detail: c.detail,
                                },
                              })
                          : undefined
                      }
                    >
                      <span className="text-foreground">{c.label}</span>
                      {c.detail && (
                        <span className="text-muted-foreground"> · {c.detail}</span>
                      )}
                    </li>
                  );
                })}
                {g.items.length > MAX_PER_GROUP && (
                  <li className="text-[10px] text-muted-foreground">
                    +{g.items.length - MAX_PER_GROUP} more
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default ExecutionSignalClusters;
